/**
 * <ExtraHoursDatesStep> — les dates exactes d'un cours ponctuel individuel (organism).
 *
 * Une ligne par heure demandée, chacune un `<DatePicker withTime>` : le jour puis l'heure
 * s'enchaînent dans un seul champ, au lieu des deux pilules du legacy
 * (`parentExtraHours/IndividualDatesStep.js`). Le format de la valeur ne bouge pas —
 * `YYYY-MM-DD HH:mm`, celui de la demande.
 *
 * En individuel, le routage automatique est court-circuité : l'équipe planifie à la main sur les
 * dates données. C'est pour ça qu'il n'y a ni grille ni échéance ici.
 *
 * Contrôlé : le brouillon arrive en prop. Le nombre de lignes suit `draft.hours` et l'écriture
 * remet toujours un tableau de la bonne longueur — la place d'une heure existe avant sa valeur.
 */
import React from "react"

import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { DatePicker } from "../../molecules/DatePicker"
import { formatDayMonthYear, toDateOnly } from "../../../ports/primary/ui/formatters/dateFormatter"
import { type UIExtraHoursDraft } from "../../../ports/primary/ui/types"

export type ExtraHoursDatesStepProps = {
    draft: UIExtraHoursDraft
    onChange: (next: UIExtraHoursDraft) => void
    /** Première date choisissable — l'équipe a besoin d'un délai pour confirmer un tuteur. */
    minDate?: Date
    /** Dernière date choisissable. */
    maxDate?: Date
    /** « Les heures sont exprimées en heure de Paris. » — seulement si le fuseau diffère. */
    timezoneNotice?: string
    disabled?: boolean
    testID?: string
}

/** « 1ʳᵉ heure », « 2ᵉ heure » — le numéro de ligne du legacy, écrit en toutes lettres. */
const rankLabel = (rank: number): string => (rank === 1 ? "1ʳᵉ heure" : `${rank}ᵉ heure`)

export function ExtraHoursDatesStep(props: ExtraHoursDatesStepProps) {
    const { draft, onChange, minDate, maxDate, timezoneNotice, disabled = false, testID } = props

    const rows = Array.from({ length: Math.max(0, draft.hours) })

    const setDate = (index: number, value: string) => {
        const next: Array<string | null> = []
        for (let i = 0; i < rows.length; i += 1) {
            next.push(i === index ? value : (draft.requestedDates[i] ?? null))
        }
        onChange({ ...draft, requestedDates: next })
    }

    const range =
        minDate != null && maxDate != null
            ? `, entre le ${formatDayMonthYear(toDateOnly(minDate))} et le ${formatDayMonthYear(toDateOnly(maxDate))}`
            : ""

    return (
        <Stack gap="5" testID={testID}>
            <Stack gap="1">
                <Text size="text-sm" color="text-muted">
                    {`Choisissez la date et l'heure exactes de chaque cours${range}.`}
                </Text>
                {timezoneNotice != null && timezoneNotice !== "" && (
                    <Text size="text-sm" weight="semibold" color="text-muted">
                        {timezoneNotice}
                    </Text>
                )}
            </Stack>

            {rows.map((_, index) => (
                <DatePicker
                    key={index}
                    label={rankLabel(index + 1)}
                    value={draft.requestedDates[index] ?? ""}
                    onChange={(value) => setDate(index, value)}
                    placeholder="Choisir une date et une heure"
                    modalLabel="Date du cours"
                    withTime
                    minDate={minDate}
                    maxDate={maxDate}
                    disabled={disabled}
                />
            ))}
        </Stack>
    )
}
